import React, { useCallback, useMemo, useState } from 'react';

import { Button, message } from 'antd';
import { PiChat, PiEye, PiRepeatFill } from 'react-icons/pi';
import TMIcon from 'tm-icons-library';

import AnalyticsEventsAPI from '../../../../api/AnalyticsEventsAPI';
import ShareBottomSheet from '../../../../components/Modals/ShareBottomSheet/ShareBottomSheet';
import {
  setLikedByUsersModalState,
  setPDPCommunityPostData,
  updateCommunitPost,
  userInterationAction,
} from '../../../../context/communityReducer';
import { useTheme } from '../../../../context/ThemeProvider';
import {
  useAppDispatch,
  useAppNavigate,
  useAppSelector,
} from '../../../../shared/hooks';
import { handleShareLink } from '../../../../shared/utils';
import { IChannelPostDetails } from '../../../../types/communityTypes';
import { PlatformIntegrationPlatformEnum } from '../../../../types/integrationType';
import { ROUTES } from '../../../../types/routes';
import LikeButton from '../../../Feed/Posts/PostItem/LikeButton';

const iconStyle = { fontSize: 24, color: '#000' };

type Props = {
  communityPostDetails: IChannelPostDetails;
  postId: string;
  channelId?: string;
  onRepostPressed?: () => void;
  isPDPPage?: boolean;
};

const CommunityPostStats: React.FC<Props> = ({
  communityPostDetails,
  postId,
  channelId: argChannelId,
  onRepostPressed,
  isPDPPage,
}) => {
  const navigate = useAppNavigate();

  const { post, ...postDetails } = communityPostDetails;
  const { selectedChannelData } = useAppSelector((state) => state.community);
  const userDetails = useAppSelector((state) => state.user);
  const channelId = useMemo(() => {
    return selectedChannelData?._id;
  }, [selectedChannelData?._id]);
  const dispatch = useAppDispatch();
  const { colors } = useTheme();
  const {
    hostMetadata: { appName },
    isTier3,
    isTier4,
  } = useAppSelector((state) => state.app);

  const [showShareBottomSheet, setShowShareBottomSheet] = useState(false);

  const handleLinkPost = useCallback(
    (isLiked: boolean) => {
      const updatedAttributes = {
        totalLikes: isLiked
          ? postDetails?.totalLikes + 1
          : postDetails?.totalLikes - 1,
        isLiked,
      };
      const actionType = isLiked ? 'addLike' : 'removeLike';
      const thunkAction =
        window.location?.pathname.includes('/channel/') ||
        window.location?.pathname.includes('/profile/')
          ? updateCommunitPost
          : setPDPCommunityPostData;
      dispatch(
        userInterationAction(
          actionType,
          communityPostDetails?._id,
          argChannelId || (channelId as string) || postDetails?.channel,
          communityPostDetails,
          updatedAttributes,
          (updatedData) => {
            dispatch(thunkAction(updatedData));
          },
        ),
      );
      try {
        AnalyticsEventsAPI.trackCommunityEvents('POST_LIKED', {
          user_id: userDetails.id,
          onboarding: userDetails.type,
          name: userDetails.name,
          email: userDetails.email,
          phone_number: userDetails.phone,
          host: userDetails.host,
          platform: PlatformIntegrationPlatformEnum.MOBILE,
          post_creator_id: post.postedBy._id,
          post_id: post._id,
          isLiked: isLiked,
          timestamp: new Date().toISOString(),
        });
      } catch (error: any) {
        console.log('Error in tracking post liked events:', error);
      }
    },
    [
      argChannelId,
      channelId,
      communityPostDetails,
      dispatch,
      post._id,
      post.postedBy._id,
      postDetails?.channel,
      postDetails?.totalLikes,
      userDetails.email,
      userDetails.host,
      userDetails.id,
      userDetails.name,
      userDetails.phone,
      userDetails.type,
    ],
  );

  return (
    <div className="community-post-stats-wrapper">
      <div className="community-post-stats-wrapper_like-wrapper">
        <LikeButton
          isLiked={postDetails?.isLiked}
          like={(like) => handleLinkPost(like)}
        />{' '}
        {post?.postSettings?.hideLikes ? null : (
          <p
            className="stats-count"
            onClick={() => {
              dispatch(
                setLikedByUsersModalState({
                  visible: true,
                  postId: communityPostDetails?._id,
                  channelId: isPDPPage
                    ? (argChannelId as string)
                    : (channelId as string) || postDetails?.channel,
                }),
              );
            }}>
            {postDetails?.totalLikes}
          </p>
        )}
      </div>

      <div className="community-post-stats-wrapper_comment-wrapper">
        <Button
          className="siteBtn noStyle"
          onClick={() => {
            if (!isPDPPage) {
              navigate(ROUTES.COMMUNITY_POST_DETAILS, {
                postId: communityPostDetails?._id,
                channelId: (channelId as string) || postDetails?.channel,
              });
            }
          }}>
          <PiChat style={iconStyle} />

          {post?.postSettings?.hideComment ? null : (
            <p className="stats-count">{postDetails?.totalComments}</p>
          )}
        </Button>
      </div>

      <div>
        <Button className="siteBtn noStyle">
          <PiEye style={iconStyle} />
          <p className="stats-count">{postDetails?.totalViews}</p>
        </Button>
      </div>

      <div className="community-post-stats-wrapper_share-wrapper">
        <Button
          className="siteBtn noStyle"
          onClick={() => {
            setShowShareBottomSheet(true);
          }}>
          <TMIcon name="share-outline" size={24} color={colors.ICON} />
        </Button>
      </div>

      {/* {hasCreatePostAccess && !hideRepostOption ? ( */}
      <div className="postRepostWrapper">
        <Button
          className="siteBtn noStyle"
          onClick={() => {
            if (!(isTier3 || isTier4)) {
              message.warning(
                'This feature is not available in the current plan. Upgrade to a higher plan to access this feature.',
                5,
              );
              return;
            }

            // Only available for Advanced and Ultimate plan users
            onRepostPressed?.();
          }}>
          <PiRepeatFill style={iconStyle} />
        </Button>
      </div>
      {/* ) : null} */}
      <ShareBottomSheet
        showModal={showShareBottomSheet}
        closeModal={() => setShowShareBottomSheet(false)}
        shareLink={handleShareLink(
          `web${ROUTES.POST_METADATA_VIEW.replace(':postId', postId)}`,
          false,
          true,
        )}
        title={`Check out the post on ${appName}`}
      />
    </div>
  );
};

export default CommunityPostStats;
